import { useState, useEffect, useCallback } from 'react';
import { Col, Row, Spin, message } from 'antd';
import PatientSearch from './components/PatientSearch';
import PatientTable from './components/PatientTable';
import PatientTablePeru from './components/PatientTablePeru';
import { admissionPatientService } from '../../services/admissionPatient';
import { getCookie } from '../../services/cookies';
import { useTranslation } from 'react-i18next';
import UnifiedSearchFilters from '../../components/UnifiedSearchFilters/UnifiedSearchFilters';
import countrySettings from './countrySettings.json';
import moment from 'moment';
import { Link } from 'react-router-dom';
import History from '../components/History';

const PatientPage = () => {
  const [loading, setLoading] = useState(
    {
      list: false,
      delete: false,
      detail: false
    }
  );
  const countryCode = getCookie("country_code");
  const [searchParams, setSearchParams] = useState(() => {
    if (countrySettings?.useUnifiedFilters?.[countryCode]) {
      return {
        branch_office_id: getCookie('branch_office_id'),
        start_date: moment().format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
        page: 1
      };
    }
    return { page: 1 };
  });
  const [patients, setPatients] = useState([]);
  const [prestations, setPrestations] = useState();
  const roles = getCookie('roles') || [];
  const allBranchOffices = roles && roles.includes('direccion_medica');
  const { t } = useTranslation();
  const historyParams = {
    data: { type: 'admission_patients' },
    types: ['admission_patients']
  };

  const fetchAdmissionPatient = useCallback(async () => {
    setLoading(loading => { return { ...loading, list: true } });
    const response = await admissionPatientService.listAdmissionPatient(searchParams);
    setPatients(response);
    setLoading(loading => { return { ...loading, list: false } });
  }, [searchParams]);

  const loadMore = (event) => {
    const direction = event.target.value;
    setSearchParams(prev => ({
      ...prev,
      page: direction === 'next' ? (prev.page || 1) + 1 : (prev.page || 1) - 1
    }));
  };

  const handlePageChange = (pageNumber) => {
    setSearchParams((previousSearchParams) => ({
      ...previousSearchParams,
      page: pageNumber,
    }));
  };

  const reloadSearch = (params) => {
    const queryParams = {
      company_rut: params.company_rut ? params.company_rut.trim().replace(/\./g, '').split('-')[0] : params.company_rut,
      reservation_number: params.reservation_number ? params.reservation_number.trim() : params.reservation_number,
      patient_rut: params.patient_rut ? params.patient_rut.trim().replace(/\./g, '').split('-')[0] : params.patient_rut,
      admission_date: params.admission_date && params.admission_date.format('YYYY-MM-DD')
    };

    const paramValues = Object.fromEntries(
      Object.entries(queryParams).filter(([_, values]) => values || (values && values.length > 0))
    );

    if (Object.values(paramValues).length > 0) {
      setSearchParams({ ...paramValues, page: 1 });
    } else {
      setSearchParams({ page: 1 });
    }
  }

  const reloadSearchPeru = (values) => {
    setSearchParams({ ...(values || {}), page: 1 });
  };

  const fetchAdmissionPatientPrestation = async (params) => {
    setLoading(loading => { return { ...loading, detail: true } });
    const response = await admissionPatientService.admissionPatientPrestation(params);
    setPrestations(response);
    setLoading(loading => { return { ...loading, detail: false } });
  }

  const deletePatient = async (params) => {
    setLoading(loading => { return { ...loading, list: true } });
    const response = await admissionPatientService.deletePatient(params);
    if (response === 1) {
      fetchAdmissionPatient();
      message.success({
        content: 'Ingreso eliminado exitosamente',
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
    } else {
      message.error({
        content: 'Ocurrió un error. Inténtalo de nuevo.',
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
      setLoading(loading => { return { ...loading, list: false } });
    }
  }

  const changeReservationNumber = async (params, values) => {
    setLoading(loading => { return { ...loading, list: true } });
    const response = await admissionPatientService.changeReservationNumber(params, values);
    if (response === 1) {
      fetchAdmissionPatient();
      message.success({
        content: `Número de ${t('admissionPatientPage.patientMain.reservation').toLocaleLowerCase()} cambiado exitosamente`,
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
    } else {
      message.error({
        content: 'Ocurrió un error. Inténtalo de nuevo.',
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
      setLoading(loading => { return { ...loading, list: false } });
    }
  }

  const deletePatientExams = async (params) => {
    setLoading(loading => { return { ...loading, detail: true } });
    const response = await admissionPatientService.deletePatientExams(params);
    if (response === 1) {
      fetchAdmissionPatientPrestation({ 'admission_patient_id': params.admission_patient_id });
      message.success({
        content: 'Ingreso eliminado exitosamente',
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
    } else {
      message.error({
        content: 'Ocurrió un error. Inténtalo de nuevo.',
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
      setLoading(loading => { return { ...loading, detail: false } });
    }
  }

  useEffect(() => {
    fetchAdmissionPatient();
  }, [fetchAdmissionPatient]);

  return (
    <Row justify="center" className='patient-page'>
      {countrySettings?.useUnifiedFilters?.[countryCode] ? (
        <Col lg={24} xs={24}>
          <Row justify="center" className="patient-search">
            <Col lg={24} xs={24} className="patient-page-wrapper__header pt-20">
              <Row justify="center">
                <Col lg={22} xs={22}>
                  <Row justify="space-between" align="middle">
                    <Col className="back-button-wrapper">
                      <Link to="/reservas/fecha">
                        <i className="icon-system-chevron-left" />
                        Volver
                      </Link>
                    </Col>
                    <Col>
                      <History params={historyParams} />
                    </Col>
                    <Col lg={24} xs={24}>
                      <h1 className="text-center mb-10">Ingresos</h1>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col lg={22} xs={22} className="mt-lg-30 mt-md-40 mt-sm-40 mt-xs-40 mb-10">
              <UnifiedSearchFilters
                moduleName="Ingresos"
                baseFilters={['company', 'outsourcing', 'thirdCompany', 'branchOffice', 'workerId', 'workerName', 'reservationNumber', 'protocol', 'dateRange', 'evaluationType', 'allBranchOffices']}
                customFilters={['consultationResult']}
                isCollapsible={true}
                userHasSystemWideAccess={allBranchOffices}
                onSearch={reloadSearchPeru}
                loading={loading.list}
                onClear={() => {
                  setSearchParams(countrySettings?.useUnifiedFilters?.[countryCode] ? {
                    branch_office_id: getCookie('branch_office_id'),
                    start_date: moment().format('YYYY-MM-DD'),
                    end_date: moment().format('YYYY-MM-DD'),
                    page: 1
                  } : { page: 1 });
                }}
                initialValues={{
                  dates: [moment(), moment()],
                  branch_office_id: parseInt(getCookie('branch_office_id')),
                  allBranchOffices: false
                }}
              />
            </Col>
          </Row>
        </Col>
      ) : (
        <PatientSearch
          reloadSearch={reloadSearch}
          countryCode={countryCode}
          historyParams={historyParams}
        />
      )}
      {
        loading.list ?
          <Spin className="cmt-spin margin-top" />
          : countrySettings?.useGridTable?.[countryCode] ? (
            <PatientTablePeru
              loading={loading}
              patients={patients}
              onPageChange={handlePageChange}
              fetchAdmissionPatientPrestation={fetchAdmissionPatientPrestation}
              prestations={prestations}
              deletePatient={deletePatient}
              deletePatientExams={deletePatientExams}
              changeReservationNumber={changeReservationNumber}
              countryCode={countryCode}
              roles={roles}
            />
          ) : (
            <PatientTable
              loading={loading}
              patients={patients}
              loadMore={loadMore}
              fetchAdmissionPatientPrestation={fetchAdmissionPatientPrestation}
              prestations={prestations}
              deletePatient={deletePatient}
              deletePatientExams={deletePatientExams}
              changeReservationNumber={changeReservationNumber}
              countryCode={countryCode}
            />
          )
      }
    </Row>
  );
};

export default PatientPage;